/**
 * admin.ts — служебная панель администратора (WEB-1900, этап 5 локализации
 * кабинета WEB-1874).
 *
 * ПОЧЕМУ ОТДЕЛЬНЫЙ ФАЙЛ. Панель — свой раздел со своей лексикой: пульс
 * бизнеса, очереди BullMQ, баланс-инвариант, разборы денежных случаев. С
 * клиентскими разделами она не пересекается ничем, кроме общих слов
 * («Отмена», «Сохранить»), которые берутся из `common`, а не заводятся здесь
 * заново.
 *
 * ЧТО ЗДЕСЬ НЕ ПЕРЕВОДИТСЯ. Имена собственные и машинные значения остаются
 * как есть на обоих языках: Stripe, Cloudflare, Anthropic, BullMQ, Redis,
 * Better Stack, коды тарифов (FREE/PRO; BUSINESS — только в старых лотах), системные роли
 * (USER/ADMIN), имена очередей, коды вердиктов генераций, `lots == ledger`,
 * `click_id`/`guid`, `MANUAL-RECONCILE`. Их читают глазами инженера и ищут по
 * буквам — перевод сделал бы поиск невозможным.
 *
 * ⚠ КОДЫ ОТКАЗОВ НЕ ТРОГАЮТСЯ. `ADMIN_2FA_REQUIRED` и
 * `UNFREEZE_REASON_REQUIRED` — машинные значения протокола; здесь переведены
 * только ФРАЗЫ, которые по ним показываются (граница карточки WEB-1900, п.4).
 *
 * Даты, числа и суммы — через `i18n/format.ts`, а не через свои `Intl`:
 * панель показывает те же значения, что до перевода, но в формате языка
 * кабинета (критерий приёмки 3 карточки WEB-1900).
 */
import { defineDictionary } from '../define';
import { plural } from '../plural';

export const adminStrings = defineDictionary(
  {
    // ── Оболочка панели: боковое меню, шапка, переключатель темы ──────────
    navGroupHealth: 'HEALTH',
    navGroupManage: 'MANAGEMENT',
    navPulse: 'Business pulse',
    navErrors: 'Errors',
    navMoney: 'Money',
    navAvailability: 'Availability',
    navQueues: 'Server jobs',
    navAiQuality: 'AI quality',
    // WEB-1992 — раздел «Скорость воронок».
    navVitals: 'Funnel speed',
    // WEB-2064 шаг 1 — раздел «Скорость ручек».
    navLatency: 'Endpoint speed',
    // WEB-2898 — раздел «Conveyor»: конвейер пачки и команды оркестратору.
    navConveyor: 'Conveyor',
    navClients: 'Clients',
    navProjects: 'Projects',
    navAudit: 'Audit',
    navBilling: 'Billing',
    notificationsSoonTooltip: 'Notifications are coming soon',
    notificationsSoonAria: 'Notifications (coming soon)',
    userMenuProfile: 'Profile',
    userMenuSettings: 'Settings',
    themeLight: 'Light theme',
    themeDark: 'Dark theme',
    pulseLoadFailed: 'Could not load the business pulse',
    // ── «Качество ИИ»: вердикты, худшие генерации, сработавшие защиты ─────
    verdictCardTitle: 'Owner verdicts',
    verdictLoadFailed: 'Could not load the verdict summary',
    verdictTotal: 'Generations total',
    verdictAccepted: 'Accepted',
    verdictAcceptedEdited: 'Accepted with edits',
    verdictRegenerated: 'Regenerated',
    verdictAbandoned: 'Abandoned',
    verdictNone: 'No verdict',

    worstCardTitle: 'Worst generations for the period',
    worstLoadFailed: 'Could not load the worst generations',
    worstEmpty: 'No generations for the period',
    worstColGeneration: 'Generation',
    worstColMode: 'Mode',
    worstColVerdict: 'Verdict',
    worstVerdictMissing: 'no verdict',
    worstColBadness: 'Badness',
    worstColGuardFires: 'Guards (times)',
    worstColEnglishLeaks: 'English strings',
    worstColImagesFailed: 'Image failures',
    worstColWhen: 'When',

    guardsCardTitle: 'Guards that fired',
    guardsLoadFailed: 'Could not load the guards summary',
    guardsEmpty: 'No guard fired during the period',
    guardsColGuard: 'Guard',
    guardsColGenerations: 'Fired (generations)',
    guardsColFires: 'Fired (times)',
    guardsFootnote: 'Rolled up over the weeks of the window — no trend chart.',

    outboxRequeueButton: 'Return to processing',
    outboxRequeueConfirmTitle: 'Return to the scheduled retry?',
    outboxRequeueConfirmDescription:
      'The action is bulk: it restarts delivery of every event that has used up its retry limit. ' +
      'Press it once the cause of the failure has been dealt with.',
    outboxRequeueConfirmOk: 'Yes, return them',
    outboxRequeueFailed: 'Could not return the rows to processing',
    outboxRequeueResult: (
      requeued: number,
      remaining: number,
      erasedSkipped: number,
    ) =>
      `Returned ${requeued}, left ${remaining}; ` +
      `skipped ${erasedSkipped} erased on request (they cannot be reprocessed)`,
    // ── Общие для нескольких экранов состояния ───────────────────────────
    statusWorking: 'working',
    statusFailure: 'failure',

    // ── «Доступность» ────────────────────────────────────────────────────
    availLoadFailed: 'Could not load availability',
    availLast24h: 'over the last 24 hours',
    domainStatusActive: 'active',
    domainStatusProvisioning: 'provisioning',
    domainStatusStuck: 'stuck',
    domainStatusExpiring: 'expiring',
    availNotFoundRate: '404 rate of funnels',
    availReasonsNotCollected: 'Reasons for “not found” are not collected yet',
    availNotFoundByReason: (total: number) =>
      `“Not found” by reason (${total} in the period)`,
    availNotFoundRecent: 'Latest “Not found”',
    availNoData: 'No data',
    availColUrl: 'Funnel address',
    availColReason: 'Reason',
    availColWhen: 'When',
    availColDomain: 'Domain',
    availColStatus: 'Status',
    availColProvisioning: 'In provisioning',
    availHours: (hours: number) => `${hours} h`,
    availColRenewal: 'Renewal',
    availDomainsTitle: 'Domains',
    availWithoutZone: (count: number) =>
      `${count} without a Cloudflare zone — their pages are not served`,
    availExpiring: (count: number) => `${count} expire in ≤7 days`,
    availInfraFlagsTitle: 'Infra flags',
    availWorkersHint: 'if off, the cron jobs stay silent',
    availFlagOn: 'on',
    availFlagOff: 'off',
    availCfRouter: 'CF Worker (router)',
    availCfRouterHint: 'routing of public domains',
    // ── «Ошибки» ─────────────────────────────────────────────────────────
    errorsLoadFailed: 'Could not load the errors',
    errorsPlatformAll: 'All',
    errorsOpenBetterStack: 'Open in Better Stack',
    errorsAppsEmptyTitle: 'Better Stack application list is empty',
    errorsAppsNotConnected:
      'Better Stack is not connected — the API key is not set.',
    errorsAppsFetchFailed:
      'Could not fetch the application list from Better Stack.',
    errorsEmpty: 'No errors found',
    errorsColLast: 'Last seen',
    errorsColPlatform: 'Platform',
    errorsColLevel: 'Level',
    errorsColMessage: 'Message',
    errorsColRoute: 'Route',
    errorsColCount: 'Count',
    errorsDetailTitle: 'Error group details',
    errorsDetailMethod: 'Method',
    errorsDetailStatusCode: 'Status code',
    errorsDetailCount: 'Number of occurrences',
    errorsDetailFirstSeen: 'First seen',
    errorsDetailLastSeen: 'Last seen',
    // ── «Очереди» ────────────────────────────────────────────────────────
    queuesLoadFailed: 'Could not load the queues',
    queuesRetryDone: (count: number, queueName: string) =>
      `Retry: ${count} ${plural('en', count, { one: 'job', other: 'jobs' })} re-queued (${queueName})`,
    queuesRetryFailed: 'Could not restart the queue',
    queuesNoConnection: 'no connection',
    queuesColQueue: 'Queue',
    queuesColAction: 'Action',
    queuesUnavailableTitle: 'No connection to some of the queues',
    queuesUnavailableText:
      'The state of those queues is unknown (the poll did not go through) — this does NOT mean they are empty.',
    queuesFailedTitle: 'Some queues have failed jobs',
    queuesFailedText:
      'Check the queues with failed > 0 and restart the failed jobs.',
    queuesCardTitle: 'Queues (BullMQ)',
    // ── «Устаревшие копии края» (WEB-1989, критерий 8) ───────────────────
    edgeStaleTitle: 'Stale edge copies (24 h)',
    edgeStaleHint:
      'How many times the edge worker served the last good copy instead of a fresh answer: the visitor saw the funnel, but our API was silent and payments did not go through at those moments.',
    edgeStaleColHost: 'Domain',
    edgeStaleColCount: 'Times served',
    edgeStaleTotal: 'Total over 24 h',
    edgeStaleNone: 'No copies served over the last 24 hours',
    edgeStaleBlind:
      'Could not read the counter — the numbers below mean nothing right now (this is "not checked", not "all good").',
    edgeStaleLoadFailed: 'Could not load the stale-copy counter',
    // ── «Скорость воронок» (WEB-1992) ────────────────────────────────────
    vitalsLoadFailed: 'Could not load funnel speed data',
    vitalsCardTitle: 'Core Web Vitals of real visitors',
    vitalsWindowDay: 'Last 24 h',
    vitalsWindowWeek: '7 days',
    vitalsColFunnel: 'Funnel',
    vitalsColHost: 'Domain',
    vitalsColScreen: 'Screen',
    vitalsColMetric: 'Metric',
    vitalsColSamples: 'Samples',
    vitalsSamplesHint:
      'Samples, not visitors: one visitor reports several measurements per step.',
    vitalsThresholdsHint:
      'Colour follows the Google p75 thresholds: LCP <= 2.5 s, INP <= 200 ms, CLS <= 0.1.',
    // WEB-2176 — «разница по шагу» рядом с накопленным p75.
    vitalsColStepDiff: 'Per-step diff',
    vitalsStepDiffHint:
      'p75 of what was added on THAT screen alone, not accumulated since the start of the page — this is what shows which screen actually jumps.',
    // WEB-2189 — разрез по шагам воронки.
    vitalsStepsCardTitle: 'By funnel step: which screen jumps',
    vitalsStepsHint:
      'One table per funnel, steps numbered from one across the quiz and the paywall. Only measurements with the current step numbering are shown: older rows counted the paywall differently and mixing them would be a lie.',
    vitalsColStep: 'Step',
    vitalsWorstStep: 'worst step',
    vitalsEmpty: 'No measurements yet',
    vitalsEmptyHint:
      'Nothing has been collected in this window. An empty table is not "fast" — it is "no data".',
    vitalsLcpCardTitle: 'What becomes the LCP element',
    vitalsColElement: 'Element',
    vitalsColElementType: 'Type',
    vitalsNoElement: 'not reported',
    // ── «Скорость ручек» (WEB-2064 шаг 1) ────────────────────────────────
    latencyLoadFailed: 'Could not load endpoint latency',
    latencyCardTitle: 'Slowest endpoints',
    latencyWindowHour: 'Last hour',
    latencyWindowDay: 'Last 24 h',
    latencyColRoute: 'Endpoint',
    latencyColKind: 'Marked',
    latencyColRequests: 'Requests',
    latencyColErrors: '5xx share',
    // Подписи видов ручек. Ключи считаются из реестра на сервере
    // (http-latency-route-kinds.ts) — двусторонний сторож
    // backend/src/shared/guards/http-latency-route-kinds-ends.web2064.spec.ts
    // краснеет и на виде без подписи, и на подписи без вида.
    latencyKindFunnelShow: 'funnel display',
    latencyKindMoney: 'money',
    latencyKindOther: '—',
    latencyEmpty: 'No measurements yet',
    latencyEmptyHint:
      'Nothing has been measured in this window. An empty table is not "fast" — it is "no data".',
    latencyBlind:
      'Could not read the latency counter — the numbers below mean nothing right now (this is "not checked", not "all good").',
    latencyQuantileHint:
      'Quantiles are bucket upper bounds: "p95 = 400 ms" means 95% of requests fitted into 400 ms, not exactly 400.',
    latencyThresholdHint: (ms: number, minutes: number) =>
      `An alert fires when p95 stays above ${ms} ms for ${minutes} minutes in a row.`,
    // ── «Деньги» ─────────────────────────────────────────────────────────
    moneyLoadFailed: 'Could not load the money data',
    moneyReviewsLoadFailed: 'Could not load the list of cases',
    moneyReviewAlreadyResolved: 'This case had already been reviewed',
    moneyReviewMarked: 'Marked as reviewed',
    moneyReviewFailed: 'Could not mark the case as reviewed',
    moneyReconcileOk: 'All organizations match (refreshed)',
    moneyReconcileMismatch: (count: number) =>
      `Mismatch in ${count} ${plural('en', count, { one: 'organization', other: 'organizations' })}`,
    moneyReconcileFailed: 'Could not re-check the balance invariant',
    moneyColClient: 'Client',
    moneyColAmount: 'Amount',
    moneyColTier: 'Plan',
    moneyColStatus: 'Status',
    moneyStatusSuccess: 'success',
    moneyTierCutTag: 'tier cut',
    moneyColTime: 'Time',
    moneyColOrg: 'Organization',
    moneyColDiff: 'Difference',
    moneyColProject: 'Project',
    moneyColModel: 'Model',
    moneyColWhen: 'When',
    moneyColProviderCharged: 'Paid to the provider',
    moneyColWhatBurns: 'What is on fire',
    moneyColReference: 'Operation key',
    moneyColReason: 'Reason',
    moneyColSince: 'Since when',
    moneyResolveButton: 'Reviewed',
    moneyReviewsCardTitle: 'Cases awaiting closure',
    moneyReviewsLoading: 'Loading the list…',
    moneyReviewsEmpty: 'There are no unreviewed money cases',
    moneyReviewsNote:
      '“Reviewed” fixes nothing and moves no money — it is a mark saying ' +
      '“a person looked at this and decided outside the system”. Who closed it, and when, is kept in the row itself.',
    moneyReviewKindPartialRefund: 'Refund: decision on credits',
    moneyReviewKindTopupUnfulfilled: 'Paid for, package not granted',
    moneyReviewKindCreditDebt: 'Credit debt',
    // WEB-2347 — второй перечень: что автоматика разобрала сама.
    moneyAutoResolvedCardTitle: 'Closed automatically',
    moneyAutoResolvedWindow: (days: number) =>
      `over the last ${days} ${plural('en', days, { one: 'day', other: 'days' })}`,
    moneyAutoResolvedLoading: 'Loading the list…',
    moneyAutoResolvedEmpty: 'Automation has closed nothing over this window',
    moneyAutoResolvedLoadFailed:
      'Could not load the list of automatically closed cases',
    moneyAutoResolvedNote:
      'These rows need no action: the money has already moved back or the ' +
      'credits have been granted. The list is here so that the work of the ' +
      'automation is visible — a closed row disappears from “Cases awaiting closure”.',
    moneyColOutcome: 'What the automation did',
    moneyColRefundId: 'Refund',
    moneyColOriginalRow: 'Original row',
    moneyColClosedAt: 'Closed at',
    moneyOutcomeAutoCredited: 'Credits granted',
    moneyOutcomeAutoRefunded: 'Money refunded',
    moneyOutcomeDuplicate: 'Repeat of a compensated payment',
    moneyPaymentsCardTitle: 'Platform payments',
    moneySuccessful: 'Successful',
    moneyFailures: 'Failures',
    moneyTierCuts: 'Tier cuts',
    moneyPaymentsTotal: 'Payments in total',
    moneyTierCutNote:
      'Rows with a tier cut: the payment went through, but the plan was not activated. The table shows the last 100.',
    moneyInvariantTitle: 'Balance invariant',
    moneyReconcileButton: 'Re-check',
    moneyInvariantOk: 'All organizations match',
    moneyAiCardTitle: 'AI spend (Anthropic)',
    moneyToday: 'Today',
    moneyWeek: 'Week',
    moneyMonth: 'Month',
    moneySpendBreakdown: 'Spend breakdown',
    moneyImages: 'images',
    moneyFailedGenNote:
      'FAILED generations — paid to the provider, charged the client 0',
    moneyNoFailures: 'There were no failures',
    // ── «Пульс бизнеса» ──────────────────────────────────────────────────
    statusOk: 'ok',
    pulseSectionUnavailable: 'Section unavailable',
    pulseBurningNow: 'On fire right now',
    pulseUpdatedAt: (time: string) => `Updated at ${time}`,
    pulseUpdatedUnknown: 'Not updated yet',
    pulseRefresh: 'Refresh',
    pulseAllCalm: 'All calm — no critical signals',
    pulseMore: 'Details →',
    pulseBusinessSignals: 'Business signals',
    pulseEarningTitle: 'Are we earning?',
    pulseRevenue24: 'Revenue over 24 h',
    pulseSubsActive: 'Active subscriptions',
    pulseGrantSpend: 'Gift credits spent',
    pulsePayments24: 'Payments over 24 h',
    pulsePayOk: (count: number) => `${count} ok`,
    pulsePayFail: (count: string) => `${count} failed`,
    pulseTierCutsLabel: 'Tier cuts',
    pulseTierCutsCount: (count: number) =>
      `${count} ${plural('en', count, { one: 'case', other: 'cases' })}`,
    pulseTierCutsNone: 'none',
    // WEB-2503: второй рубеж цены тарифа — цена подписки не из ценового плана.
    pulseOffPlanPriceLabel: 'Price not from the plan',
    pulseOffPlanPriceCount: (count: number) =>
      `${count} ${plural('en', count, { one: 'event', other: 'events' })}`,
    pulseOffPlanPriceNone: 'none',
    pulseBalanceInvariant: 'Balance invariant',
    pulseClientsWorkingTitle: 'Are clients up and running?',
    pulseNotFound24: '404 rate of funnels, 24 h',
    pulseDomains: 'Domains',
    pulseDomainsActive: (count: number) => `${count} active`,
    pulseDomainsStuck: (count: number) => `${count} stuck`,
    pulseDomainsExpiring: (count: number) => `${count} expire in ≤7 d`,
    pulseBudgetTitle: 'Are we burning the budget?',
    pulseAiSpendLink: 'AI spend',
    pulseSpendToday: 'Spend today',
    pulseSpendMonth: 'Spend this month',
    pulseBudgetNotSet: 'Monthly budget is not set',
    pulseMarginLeak: 'Margin leak',
    pulseBurnedGen: 'Generations burned',
    pulseBurnedGenTag: (count: number) => `${count} burned`,
    pulseTechTitle: 'Technical pulse',
    pulseTechDb: 'Database',
    pulseTechQueues: 'Queues',
    pulseInWork: 'in progress',
    pulseTechFeCrashes: 'FE crashes 24 h',
    pulseTechBeCrashes: 'BE crashes 24 h',
    pulseTechLastDeploy: 'Last deploy',
    pulseTechDbPool: 'DB pool',
    pulseBusy: 'busy',
    pulseFree: 'free',
    pulseWaiting: (count: number) => `${count} waiting`,
    pulseLegacyGuid: 'Legacy guid, 24 h',
    pulseLegacyBreakdown: (read: number, mutate: number, paywall: number) =>
      `reads ${read}, money ${mutate}, checkout ${paywall}`,
    pulseLegacyUnverified: 'reported by the client, not verified:',
    pulseLegacyByProject: 'checkout by project:',
    pulseAnalyticsSpill: 'Analytics outside sections',
    pulseRotationStalled: 'rotation may have stalled',
    // WEB-2670: the value's probe did not finish within the deadline — shown
    // instead of the placeholder number the server sends for it.
    pulseProbeTimedOut: (seconds: number) => `no answer within ${seconds} s`,
    pulseSpillNotCounted: (seconds: number) =>
      `not counted within ${seconds} s`,
    // ── «Аудит» (журнал действий администраторов) ────────────────────────
    // ⚠ Множество действий и типов объектов НЕ МЕНЯЕТСЯ через словарь: оно
    // сверяется с серверным enum сторожем WEB-1357. Здесь только подписи.
    auditActionCreateUser: 'Client created',
    auditActionUpdateUser: 'Client changed',
    auditActionDeleteUser: 'Client deactivated',
    auditActionRestoreUser: 'Client restored',
    auditActionChangeUserPassword: 'Client password changed',
    auditActionViewUser: 'Client data viewed',
    auditActionCreateProject: 'Project created',
    auditActionUpdateProject: 'Project changed',
    auditActionDeleteProject: 'Project deleted',
    auditActionAddProjectMember: 'Member added',
    auditActionUpdateProjectMember: 'Member role changed',
    auditActionRemoveProjectMember: 'Member removed',
    auditActionAdjustBalance: 'Balance adjusted',
    auditActionChargebackUnfreeze: 'Unfrozen after a chargeback',
    auditActionAdminCredit: 'Credits granted',
    auditActionGrantTier: 'Plan granted',
    auditActionTierChange: 'Plan changed (automatic)',
    auditActionQueueRetry: 'Queue job retried',
    auditActionRevokePreviewLinks: 'Preview links revoked',
    auditActionPricingPlanVersionCreated: 'Pricing plan version saved',
    auditActionEnterpriseAddonInvoiceCreated: 'Enterprise add-on invoice issued',
    auditEntityUser: 'Client',
    auditEntityProject: 'Project',
    auditEntityProjectMember: 'Project member',
    auditEntityBalance: 'Client balance',
    auditEntitySubscription: 'Organization',
    auditEntityQueue: 'Queue',
    auditEntityPricingPlan: 'Pricing plan (organization)',
    auditDetailSelfService:
      'Administrator self-service (member of the organization)',
    auditDetailTargetIsAdmin: 'Target is a system administrator',
    auditDetailSelf: 'Action on the administrator’s own account',
    auditDetailAmountCents: 'Amount, cents',
    auditDetailDescription: 'Reason',
    auditDetailProjectId: 'Project',
    auditDetailOrgId: 'Organization',
    auditDetailLotId: 'Lot',
    auditDetailFromTier: 'Plan before',
    auditDetailToTier: 'Plan after',
    auditDetailTargetUserId: 'Client (id)',
    auditDetailTargetUserEmail: 'Client email',
    auditDetailEmail: 'Email',
    auditDetailUsername: 'Name',
    auditDetailSystemRole: 'System role',
    auditDetailQueueName: 'Queue',
    auditDetailRole: 'Role',
    auditDeletedBadge: 'deleted',
    auditLoadFailed: 'Could not load the audit log',
    auditColTime: 'Time',
    auditColAdmin: 'Admin',
    auditColAction: 'Action',
    auditColEntity: 'Object',
    auditColTarget: 'Acted on',
    auditDetailsButton: 'Details',
    auditFilterAllAdmins: 'All admins',
    auditFilterAllActions: 'All actions',
    auditEmpty: 'No records',
    auditDrawerTitle: 'Event details',
    auditEntityTypeLabel: 'Object type',
    auditTargetClient: 'Client',
    auditEventContent: 'Event content',
    auditNoDetails: 'The writer left no details',
    // ── «Биллинг» (поиск клиента, баланс, снятие заморозки) ──────────────
    billingSearchFailed: 'Could not find the client',
    billingSearchCardTitle: 'Client search and balance',
    billingEmailPlaceholder: 'Client email',
    billingSearchButton: 'Search',
    billingSearchEmpty: 'Enter an email and press “Search”',
    billingColRole: 'Role',
    billingSelectButton: 'Select',
    billingBalanceOf: (email: string) => `Balance: ${email}`,
    billingAdjustBalance: 'Adjust the balance',
    billingChargebackCardTitle: 'Chargeback — unfreezing',
    billingProjectIdPlaceholder: 'Project ID',
    billingUnfreezeConfirmTitle: 'Unfreeze the project?',
    billingUnfreezeConfirmOk: 'Confirm',
    billingUnfreezeButton: 'Unfreeze',
    billingUnfrozen: 'The project has been unfrozen',
    billingUnfreezeFailed: 'Could not unfreeze the project',
    // ⚠ Код отказа `UNFREEZE_REASON_REQUIRED` не трогается (граница WEB-1900):
    // это запасная фраза на случай, когда сервер прислал отказ без текста.
    billingUnfreezeReasonRequired:
      'The dispute at the payment provider is not closed — unfreezing requires a reason.',
    billingReasonModalTitle: 'Reason for unfreezing',
    billingReasonSubmit: 'Unfreeze',
    billingReasonPlaceholder:
      'For example: the dispute was settled with the bank directly, the client sent the bank letter',
    billingReasonHint: (min: number, max: number) =>
      `From ${min} to ${max} characters. The reason goes into the organization log and to the owner by email.`,
    // WEB-1346 (заход 2): кредиты за оспоренные платежи и отметка о списании.
    billingCreditsLoadFailed:
      'Could not find out how many credits were granted for the disputed payments',
    billingWriteOffModalTitle: 'Credits for disputed payments',
    billingWriteOffIntro:
      'Credits were granted for the payments of these disputes. They are not debited automatically: check the balance and mark whether the debit has been done.',
    billingWriteOffUnknownNote:
      'For some disputes the amount could not be determined (the payment provider did not answer). The mark is still required.',
    billingWriteOffColDispute: 'Dispute',
    billingWriteOffColStatus: 'Status',
    billingWriteOffColGranted: 'Granted',
    billingWriteOffColRemaining: 'Not spent yet',
    billingWriteOffStatusOpen: 'open',
    billingWriteOffStatusLost: 'lost',
    billingWriteOffUnknown: 'amount unknown',
    billingWriteOffTotal: (sum: string) => `Known total: ${sum}`,
    billingWriteOffDone: 'Debit done',
    billingWriteOffNotRequired: 'No debit needed',
    billingWriteOffSubmit: 'Unfreeze',
    // ── Организации клиента (выбор в окнах денег и тарифа) ───────────────
    orgsLoadFailed: 'Could not find the client’s organizations',
    orgRoleOwner: 'owner',
    orgRoleAdmin: 'admin',
    orgRoleMember: 'member',
    orgProjectCount: (count: number) =>
      `${count} ${plural('en', count, { one: 'project', other: 'projects' })}`,

    // ── Окно «История входов» ────────────────────────────────────────────
    loginMethodPassword: 'password',
    loginMethodTwoFactor: 'two-factor code',
    loginColTime: 'Time',
    loginColDevice: 'Device',
    loginColMethod: 'Sign-in method',
    loginHistoryTitle: (userLabel: string) => `Sign-in history — ${userLabel}`,
    loginHistoryNotice:
      'The log has been kept since it appeared in the system. Sign-ins that happened earlier are not in it.',
    loginHistoryLoadFailed: 'Could not load the sign-in history',
    loginHistoryEmptyError: 'The sign-in history could not be loaded',
    loginHistoryEmpty: 'No sign-in has been recorded',
    // ── Окна денег и тарифа (общие поля выбора организации) ──────────────
    modalClient: 'Client',
    modalOrganization: 'Organization',
    modalOrgRequired: 'Choose an organization',
    modalOrgSearching: 'Looking for organizations…',
    modalOrgPlaceholder: 'Choose an organization',
    modalOrgNotFound: 'The client has no organizations',
    modalReason: 'Reason',
    modalReasonRequired: 'State the reason',

    // ── Окно «Корректировка баланса» ─────────────────────────────────────
    adjustAmountRequired: 'State the amount',
    adjustAmountWholeCredits: 'State a whole number of credits (1 Cr = 1 cent)',
    adjustAmountCentPrecision: 'State the amount to the cent',
    adjustAmountNonZero: 'The adjustment amount cannot be zero',
    adjustAmountMaxDollars: (max: string) =>
      `The amount cannot exceed $${max} per operation`,
    adjustAmountMaxCredits: (max: string) =>
      `The amount cannot exceed ${max} Cr per operation`,
    adjustDone: (amount: string) => `Balance adjusted: ${amount}`,
    adjustFailed: 'Could not adjust the balance',
    adjustModalTitle: 'Balance adjustment',
    adjustApply: 'Apply',
    adjustOrgHint:
      'The wallet belongs to the organization — the adjustment applies to its balance.',
    adjustUnit: 'Unit',
    adjustAmountLabel: (unit: string) => `Amount (± in ${unit})`,
    adjustUnitDollars: 'dollars',
    adjustUnitCredits: 'credits',
    adjustPreviewCredit: 'Will be credited',
    adjustPreviewDebit: 'Will be debited',
    adjustPreviewRate: (credits: number) =>
      `${credits} Cr (rate 1 Cr = 1 cent)`,

    // ── Окно «Выдать/сменить тариф» ──────────────────────────────────────
    grantDone: (tier: string) => `Plan ${tier} applied`,
    grantStripeConflict:
      'The client has an active Stripe subscription, cancel it first.',
    grantFailed: 'Could not grant the plan',
    grantModalTitle: 'Grant or change the plan',
    grantSubmit: 'Grant',
    grantOrgHint:
      'The organization whose subscription we change (manually, without Stripe). Owned ones come first.',
    grantTierLabel: 'Plan',
    grantTierRequired: 'Choose a plan',
    grantReasonHint: 'It goes into the “Audit” log together with your name.',
    modalLoading: 'Loading…',
    // ── Панель баланса клиента ───────────────────────────────────────────
    balanceLoadFailed: 'Could not load the balance',
    balanceTotalDeposited: (amount: string) => `Deposited in total: ${amount}`,
    balanceRecentTransactions: 'Recent transactions',
    balanceEmpty: 'No transactions',
    balanceColDate: 'Date',
    balanceColAmount: 'Amount',
    balanceColStatus: 'Status',
    balanceColType: 'Type',
    balanceColNote: 'Note',
    transactionStatusPending: 'in progress',
    transactionStatusCompleted: 'completed',
    transactionStatusFailed: 'failed',
    // ── Окно «Регистрация клиента» ───────────────────────────────────────
    registerRoleUser: 'User',
    registerRoleAdmin: 'Administrator',
    registerBalanceNotNumber: 'The starting balance must be a number',
    registerBalanceNegative:
      'The starting balance cannot be negative. 0 or empty means registration without a top-up',
    registerBalanceTooBig:
      'The starting balance for a single registration cannot exceed $100,000',
    registerBalanceTooPrecise:
      'No more than two decimal places: there is no amount smaller than a cent',
    registerPasswordTooShort: 'At least 8 characters',
    registerAdminConfirmTitle: 'Registering an administrator',
    registerAdminConfirmText:
      'You are creating a user with administrator rights (full access to the panel). Confirm?',
    registerDone: 'The client has been registered',
    registerFailed: 'Could not register the client',
    registerModalTitle: 'Client registration',
    registerSubmit: 'Register',
    registerEmailRequired: 'State the email',
    registerEmailInvalid: 'Enter a valid email',
    registerPasswordLabel: 'Password',
    registerPasswordHint: 'can be set later',
    registerPasswordPlaceholder: 'can be left empty',
    registerUsernameLabel: 'User name',
    registerEmailVerifiedLabel: 'Email confirmed',
    registerRoleLabel: 'Role',
    registerOrgNameLabel: 'Organization name',
    registerFromEmailHint: 'by default — from the email',
    registerProjectNameLabel: 'Project name',
    registerInitialBalanceLabel: 'Starting balance, $',
    registerInitialBalanceHint:
      '0 or empty — no top-up, a negative value is not accepted. $1 = 100 credits: the top-up arrives as credits. No more than $100,000 per registration. No more than two decimal places.',
    // ── «Проекты» ────────────────────────────────────────────────────────
    projectsLoadFailed: 'Could not load the projects',
    projectsMembersLoadFailed: 'Could not load the members',
    projectsUpdated: 'The project has been updated',
    projectsUpdateFailed: 'Could not update the project',
    projectsDeleted: 'The project has been deleted',
    projectsDeleteFailed: 'Could not delete the project',
    projectsRoleUpdated: 'The role has been updated',
    projectsRoleUpdateFailed: 'Could not update the role',
    projectsMemberRemoved: 'The member has been removed',
    projectsMemberRemoveFailed: 'Could not remove the member',
    projectsColProject: 'Project',
    projectsColMembers: 'Members',
    projectsColCreated: 'Created',
    projectsSearchPlaceholder: 'Search by project name',
    projectsEmpty: 'No projects',
    projectsNameLabel: 'Name',
    projectsNameRequired: 'State the project name',
    projectsDescriptionLabel: 'Description',
    projectsMembersTitle: 'Members',
    projectsMembersLoadError: (reason: string) =>
      `Could not load the members: ${reason}`,
    projectsMembersEmpty: 'No members',
    projectsColRole: 'Role',
    projectsRemoveMemberConfirm: 'Remove the member?',
    projectsDeleteConfirm: 'Delete the project?',
    // WEB-2114 (требование 24): массовый отзыв ссылок предпросмотра.
    previewLinksTitle: 'Preview links',
    previewLinksRevokeProjectButton: 'Revoke all preview links',
    previewLinksRevokeProjectConfirm:
      'Revoke all preview links of this project? They will stop opening for everyone right away.',
    // Отзыв по квизу или пейволлу снимает и ссылки воронок этого проекта, в
    // которых он стоит сейчас (кнопка аварийная) — подписи говорят это прямо.
    previewLinksTargetHint:
      'Or only the links of one quiz, paywall or funnel. For a quiz or a paywall this also revokes the links of the funnels of this project that currently use it:',
    previewLinksTargetQuiz: 'Quiz',
    previewLinksTargetPaywall: 'Paywall',
    previewLinksTargetFunnel: 'Funnel',
    previewLinksTargetIdPlaceholder: 'Object ID (uuid)',
    previewLinksRevokeTargetButton: 'Revoke links of the object',
    previewLinksRevokeTargetConfirm:
      'Revoke all preview links of this object? For a quiz or a paywall — also the links of the funnels of this project that currently use it. They will stop opening for everyone right away.',
    previewLinksRevoked: (count: number) => `Preview links revoked: ${count}`,
    previewLinksRevokeFailed: 'Could not revoke the preview links',
    projectsDeleteButton: 'Delete the project',
    // ── «Клиенты» ────────────────────────────────────────────────────────
    // ⚠ Предупреждение перед деактивацией по смыслу повторяет серверное
    // DEACTIVATION_SUBSCRIPTION_NOTICE (client-deactivation.service.ts):
    // сервер отвечает ПОСЛЕ действия, а предупреждать надо ДО. Меняете
    // один — меняйте оба.
    clientsDeactivateWarningMaterials:
      'The client’s materials will be locked to read-only and public serving will go dark: the funnels stop taking traffic and collecting leads. Nothing is deleted, the domains stay with the client, and the action is reversible with the “Restore” button.',
    // WEB-1945 (решение владельца 13.09): деактивация клиента с живой
    // подпиской Stripe теперь ЗАПРЕЩЕНА, а не «разрешена с предупреждением».
    clientsDeactivateWarningSubscriptionBold:
      'A client with a live Stripe subscription cannot be deactivated.',
    clientsDeactivateWarningSubscriptionRest:
      ' Deactivation does not cancel the subscription, so while Stripe can still charge the client it is refused: their sign-in would be closed and they could no longer cancel. First a support agent cancels the subscription by hand in the platform’s Stripe dashboard: Customers → the client’s card → Subscriptions → Cancel subscription — after that the deactivation goes through. A subscription the client has already cancelled at the end of the paid period does not block it.',
    clientsReportLocked: (projects: number) =>
      `Materials locked to read-only: ${projects} ${plural('en', projects, { one: 'project', other: 'projects' })}. Public serving is dark.`,
    clientsReportNotLocked:
      'Sign-in is closed, but the materials are NOT locked — the client’s funnels keep serving traffic. Repeat the deactivation; if it does not help, this is a server failure (line MANUAL-RECONCILE in the log).',
    clientsReportSkippedOrgs: (count: number) =>
      `Organizations left untouched: ${count} — they have other members, and their materials stay in service.`,
    clientsReportKeptDomains: (count: number) =>
      `Purchased domains that stay with the client: ${count} — we do not release them.`,
    clientsReportLiveSubscriptions: (count: number) =>
      `Subscriptions Stripe can still charge: ${count}. `,
    clientsReactivationUnlocked: (projects: number) =>
      `Materials are back in service: ${projects} ${plural('en', projects, { one: 'project', other: 'projects' })}.`,
    clientsReactivationNotUnlocked:
      'Sign-in is restored, but the materials are NOT unlocked — the client’s funnels stay dark. Repeat the restore; if it does not help, this is a server failure (line MANUAL-RECONCILE in the log).',
    clientsTiersLoading: 'loading…',
    clientsTierUnknown: 'unknown',
    clientsLoadFailed: 'Could not load the clients',
    clientsDetailRefreshFailed: 'Could not refresh the client data',
    clientsDeactivated: 'The client has been deactivated',
    clientsDeactivatedTitle: 'Client deactivated',
    clientsUnderstood: 'Got it',
    clientsDeactivateFailed: 'Could not deactivate the client',
    clientsRestored: 'The client has been restored',
    clientsRestoredTitle: 'Client restored',
    clientsRestoreFailed: 'Could not restore the client',
    clientsPasswordUpdated: 'The password has been updated',
    clientsPasswordChangeFailed: 'Could not change the password',
    clientsColRole: 'Role',
    clientsColEmailVerified: 'Email confirmed',
    clientsColStatus: 'Status',
    clientsStatusDeactivated: 'deactivated',
    clientsStatusActive: 'active',
    clientsColRegistered: 'Registered',
    clientsColLastLogin: 'Last sign-in',
    clientsNeverSignedIn: 'has never signed in',
    clientsLoginHistoryTooltip: 'Sign-in history',
    clientsSearchPlaceholder: 'Search by email / name',
    clientsFilterActive: 'Active',
    clientsFilterDeactivated: 'Deactivated',
    clientsFilterAll: 'All statuses',
    clientsRegisterButton: 'Register a client',
    clientsEmpty: 'No clients',
    clientsDetailUsername: 'User name',
    clientsDetailOrgTiers: 'Plans of the organizations',
    clientsBalanceDivider: 'Balance',
    clientsAdminTargetWarning:
      'This is a system administrator account — actions on it are written to the log. If it is your own account, changing the password ends the current session immediately: you will need to sign in again with the new password.',
    clientsChangePassword: 'Change the password',
    clientsAdjustBalance: 'Adjust the balance',
    clientsGrantTier: 'Grant or change the plan',
    clientsDeactivateConfirm: 'Deactivate the client?',
    clientsDeactivateButton: 'Deactivate',
    clientsRestoreButton: 'Restore',
    clientsPasswordModalTitle: 'Change the password',
    clientsNewPasswordLabel: 'New password',
    clientsNewPasswordRequired: 'State the new password',
    // ── Отказы служебных ручек, показываемые человеком-читаемой фразой ───
    // ⚠ Значение кода `ADMIN_2FA_REQUIRED` НЕ меняется (граница WEB-1900):
    // переведена только фраза, которая по нему показывается.
    admin2faRequired:
      'The admin panel now requires two-factor authentication. Turn it on in your account settings (Settings → Security), then reload the page.',
    // ── WEB-2331: «Переезд на subster.ai» — кто ещё не переехал ─────────────
    navMigration: 'Move to subster.ai',
    migrationLoadFailed: 'Could not load the migration overview',
    migrationDomainsTitle: 'Customer domains (own CNAME)',
    migrationDomainsLegacyLabel: 'On the old CNAME target',
    migrationProjectsTitle: 'Calls to the old API address, last 7 days',
    migrationProjectsLabel: 'Projects on the old API',
    // ── WEB-3469, заход 2: «Уведомления» — настройки писем каждому админу ──
    navNotifications: 'Notifications',
    notificationsHint:
      'Platform admins and which emails each of them receives. Anyone without a saved setting gets them (as before).',
    notificationsAdminColumn: 'Admin',
    notificationsConveyorMail: 'Conveyor emails',
    notificationsLoadFailed: 'Could not load notification settings',
    notificationsSaveFailed: 'Could not save the setting',
    migrationOldAddresses: (targets: string, hosts: string): string =>
      `Old CNAME targets: ${targets}. Old API hosts: ${hosts}.`,
    migrationNotSet: 'not set',
    migrationCnameOff:
      'LEGACY_CNAME_TARGETS is empty — domains are not checked, so this list proves nothing.',
    migrationApiOff:
      'LEGACY_API_HOSTS is empty — calls to the old API are not recorded, so this list proves nothing.',
    migrationColDomain: 'Domain',
    migrationColOrg: 'Organization',
    migrationColProject: 'Project',
    migrationColVerdict: 'Where it points',
    migrationColResolvedTo: 'DNS answer',
    migrationColCheckedAt: 'Checked',
    migrationColLastSeen: 'Last call',
    migrationColHits24h: 'Calls, 24 h',
    migrationColHits7d: 'Calls, 7 days',
    migrationColHost: 'Host',
    migrationVerdictLegacy: 'On the old target',
    migrationVerdictCurrent: 'Moved',
    migrationVerdictOther: 'Points elsewhere',
    migrationVerdictMissing: 'No record',
    migrationVerdictUnknown: 'Not checked yet',
    migrationLastAttemptFailed: 'last attempt: DNS did not answer',
    migrationCheckNow: 'Check now',
    migrationCheckDone: 'Checked',
    migrationCheckFailed: 'Could not check the domain',
    migrationNoDomains: 'No customer domains connected via Cloudflare for SaaS',
    migrationNoProjects:
      'Nobody called the old API address during the last 7 days',
    // WEB-2329: база возврата в приложение у проекта (см. AdminMigrationPage).
    migrationBaseTitle: 'Return-to-app base domain per project',
    migrationBaseHint:
      'The “return to app” link after payment is built from this base. Set the old base only for a project whose released app still has the old zone in Associated Domains / intent-filter — otherwise the link will not open the app.',
    migrationBaseSearchPlaceholder: 'Project name',
    migrationBaseSearchButton: 'Find',
    migrationBaseColCurrent: 'Return-to-app base',
    migrationBaseColProjectId: 'Project ID',
    migrationBaseDefaultOption: (main: string): string => `Main base (${main})`,
    migrationBaseNoProjects: 'No projects found for this query',
    migrationBaseSaved: 'Return-to-app base saved',
    migrationBaseSaveFailed: 'Could not save the return-to-app base',
    migrationBaseLoadFailed: 'Could not load the project list',
  },
  {
    // ── Оболочка панели: боковое меню, шапка, переключатель темы ──────────
    navGroupHealth: 'ЗДОРОВЬЕ',
    navGroupManage: 'УПРАВЛЕНИЕ',
    navPulse: 'Пульс бизнеса',
    navErrors: 'Ошибки',
    navMoney: 'Деньги',
    navAvailability: 'Доступность',
    navQueues: 'Фоновые задачи сервера',
    navAiQuality: 'Качество ИИ',
    navVitals: 'Скорость воронок',
    navLatency: 'Скорость ручек',
    navConveyor: 'Конвейер',
    navClients: 'Клиенты',
    navProjects: 'Проекты',
    navAudit: 'Аудит',
    navBilling: 'Биллинг',
    notificationsSoonTooltip: 'Уведомления скоро появятся',
    notificationsSoonAria: 'Уведомления (скоро)',
    userMenuProfile: 'Профиль',
    userMenuSettings: 'Настройки',
    themeLight: 'Светлая тема',
    themeDark: 'Тёмная тема',
    pulseLoadFailed: 'Не удалось загрузить пульс бизнеса',
    // ── «Качество ИИ»: вердикты, худшие генерации, сработавшие защиты ─────
    verdictCardTitle: 'Вердикты владельцев',
    verdictLoadFailed: 'Не удалось загрузить сводку вердиктов',
    verdictTotal: 'Генераций всего',
    verdictAccepted: 'Принято',
    verdictAcceptedEdited: 'Доправлено',
    verdictRegenerated: 'Перегенерировано',
    verdictAbandoned: 'Брошено',
    verdictNone: 'Без вердикта',

    worstCardTitle: 'Худшие генерации за период',
    worstLoadFailed: 'Не удалось загрузить худшие генерации',
    worstEmpty: 'Генераций за период нет',
    worstColGeneration: 'Генерация',
    worstColMode: 'Режим',
    worstColVerdict: 'Вердикт',
    worstVerdictMissing: 'без вердикта',
    worstColBadness: 'Плохость',
    worstColGuardFires: 'Защиты (раз)',
    worstColEnglishLeaks: 'Англ. строки',
    worstColImagesFailed: 'Провалы картинок',
    worstColWhen: 'Когда',

    guardsCardTitle: 'Сработавшие защиты',
    guardsLoadFailed: 'Не удалось загрузить сводку по защитам',
    guardsEmpty: 'За период защиты не срабатывали',
    guardsColGuard: 'Защита',
    guardsColGenerations: 'Сработала (генераций)',
    guardsColFires: 'Сработала (раз)',
    guardsFootnote: 'Свёрнуто по неделям за окно среза — без графика динамики.',

    outboxRequeueButton: 'Вернуть в работу',
    outboxRequeueConfirmTitle: 'Вернуть в плановый добор?',
    outboxRequeueConfirmDescription:
      'Действие пакетное: заново запускает доставку сразу всех событий, ' +
      'исчерпавших потолок повторов. Нажимайте, когда причина сбоя разобрана.',
    outboxRequeueConfirmOk: 'Да, вернуть',
    outboxRequeueFailed: 'Не удалось вернуть строки в работу',
    outboxRequeueResult: (
      requeued: number,
      remaining: number,
      erasedSkipped: number,
    ) =>
      `Возвращено ${requeued}, осталось ${remaining}; ` +
      `стёртых по запросу пропущено ${erasedSkipped} ` +
      `(разбору не подлежат)`,
    // ── Общие для нескольких экранов состояния ───────────────────────────
    statusWorking: 'работает',
    statusFailure: 'сбой',

    // ── «Доступность» ────────────────────────────────────────────────────
    availLoadFailed: 'Не удалось загрузить доступность',
    availLast24h: 'за последние 24 часа',
    domainStatusActive: 'активен',
    domainStatusProvisioning: 'провижининг',
    domainStatusStuck: 'завис',
    domainStatusExpiring: 'истекает',
    availNotFoundRate: '404-rate воронок',
    availReasonsNotCollected: 'Причины «not found» пока не собираются',
    availNotFoundByReason: (total: number) =>
      `«Not found» по причине (${total} за период)`,
    availNotFoundRecent: 'Последние «Not found»',
    availNoData: 'Нет данных',
    availColUrl: 'Адрес воронки',
    availColReason: 'Причина',
    availColWhen: 'Когда',
    availColDomain: 'Домен',
    availColStatus: 'Статус',
    availColProvisioning: 'В провижининге',
    availHours: (hours: number) => `${hours} ч`,
    availColRenewal: 'Продление',
    availDomainsTitle: 'Домены',
    availWithoutZone: (count: number) =>
      `${count} без зоны Cloudflare — не отдают страницы`,
    availExpiring: (count: number) => `${count} истекают ≤7 дней`,
    availInfraFlagsTitle: 'Инфра-флаги',
    availWorkersHint: 'если выкл — кроны молчат',
    availFlagOn: 'включён',
    availFlagOff: 'выключен',
    availCfRouter: 'CF Worker (роутер)',
    availCfRouterHint: 'маршрутизация публичных доменов',
    // ── «Ошибки» ─────────────────────────────────────────────────────────
    errorsLoadFailed: 'Не удалось загрузить ошибки',
    errorsPlatformAll: 'Все',
    errorsOpenBetterStack: 'Открыть в Better Stack',
    errorsAppsEmptyTitle: 'Список приложений Better Stack пуст',
    errorsAppsNotConnected:
      'Better Stack не подключён — ключ доступа не задан.',
    errorsAppsFetchFailed:
      'Не удалось получить список приложений Better Stack.',
    errorsEmpty: 'Ошибок не найдено',
    errorsColLast: 'Последнее',
    errorsColPlatform: 'Платформа',
    errorsColLevel: 'Уровень',
    errorsColMessage: 'Сообщение',
    errorsColRoute: 'Маршрут',
    errorsColCount: 'Кол-во',
    errorsDetailTitle: 'Детали группы ошибок',
    errorsDetailMethod: 'Метод',
    errorsDetailStatusCode: 'Статус-код',
    errorsDetailCount: 'Количество',
    errorsDetailFirstSeen: 'Впервые замечено',
    errorsDetailLastSeen: 'Последний раз замечено',
    // ── «Очереди» ────────────────────────────────────────────────────────
    queuesLoadFailed: 'Не удалось загрузить очереди',
    queuesRetryDone: (count: number, queueName: string) =>
      `Retry: ${count} ${plural('ru', count, { one: 'задача', few: 'задачи', many: 'задач' })} переотправлено (${queueName})`,
    queuesRetryFailed: 'Не удалось перезапустить очередь',
    queuesNoConnection: 'нет связи',
    queuesColQueue: 'Очередь',
    queuesColAction: 'Действие',
    queuesUnavailableTitle: 'Нет связи с частью очередей',
    queuesUnavailableText:
      'Состояние этих очередей неизвестно (опрос не прошёл) — это НЕ означает, что в них нет задач.',
    queuesFailedTitle: 'В некоторых очередях есть упавшие задачи',
    queuesFailedText:
      'Проверьте очереди с failed > 0 и перезапустите неудачные задачи.',
    queuesCardTitle: 'Очереди (BullMQ)',
    // ── «Устаревшие копии края» (WEB-1989, критерий 8) ───────────────────
    edgeStaleTitle: 'Копии с края за сутки',
    edgeStaleHint:
      'Сколько раз воркер на краю отдал последнюю хорошую копию вместо свежего ответа: посетитель воронку видел, но наш API в эти моменты молчал и оплата не проходила.',
    edgeStaleColHost: 'Домен',
    edgeStaleColCount: 'Отдач копии',
    edgeStaleTotal: 'Всего за сутки',
    edgeStaleNone: 'За сутки копии не отдавались',
    edgeStaleBlind:
      'Не смог прочитать счётчик — числа ниже сейчас ничего не значат (это «не проверял», а не «всё хорошо»).',
    edgeStaleLoadFailed: 'Не удалось загрузить счётчик копий',
    // ── «Скорость воронок» (WEB-1992) ────────────────────────────────────
    vitalsLoadFailed: 'Не удалось загрузить скорость воронок',
    vitalsCardTitle: 'Скорость у реальных посетителей',
    vitalsWindowDay: 'Сутки',
    vitalsWindowWeek: '7 дней',
    vitalsColFunnel: 'Воронка',
    vitalsColHost: 'Домен',
    vitalsColScreen: 'Экран',
    vitalsColMetric: 'Показатель',
    vitalsColSamples: 'Замеры',
    vitalsSamplesHint:
      'Замеры, а не посетители: один посетитель присылает несколько значений за шаг.',
    vitalsThresholdsHint:
      'Цвет — по порогам Google для p75: LCP <= 2,5 с, INP <= 200 мс, CLS <= 0,1.',
    // WEB-2176 — «разница по шагу» рядом с накопленным p75.
    vitalsColStepDiff: 'Разница по шагу',
    vitalsStepDiffHint:
      'p75 того, что добавилось именно на ЭТОМ экране, а не накопилось с начала страницы — по нему видно, какой экран прыгает на самом деле.',
    // WEB-2189 — разрез по шагам воронки.
    vitalsStepsCardTitle: 'По шагам воронки: какой экран прыгает',
    vitalsStepsHint:
      'Своя таблица на каждую воронку, номера шагов сквозные — с единицы, общие у квиза и экрана оплаты. Показаны только замеры текущей нумерации шага: у старых строк пейволл считался иначе, и смешивать их значило бы врать.',
    vitalsColStep: 'Шаг',
    vitalsWorstStep: 'худший шаг',
    vitalsEmpty: 'Замеров пока нет',
    vitalsEmptyHint:
      'За это окно ничего не собрано. Пустая таблица — это не «быстро», а «данных нет».',
    vitalsLcpCardTitle: 'Что становится элементом LCP',
    vitalsColElement: 'Элемент',
    vitalsColElementType: 'Тип',
    vitalsNoElement: 'не сообщён',
    // ── «Скорость ручек» (WEB-2064 шаг 1) ────────────────────────────────
    latencyLoadFailed: 'Не удалось загрузить скорость ручек',
    latencyCardTitle: 'Самые медленные ручки',
    latencyWindowHour: 'Час',
    latencyWindowDay: 'Сутки',
    latencyColRoute: 'Ручка',
    latencyColKind: 'Пометка',
    latencyColRequests: 'Запросов',
    latencyColErrors: 'Доля 5xx',
    latencyKindFunnelShow: 'показ воронки',
    latencyKindMoney: 'деньги',
    latencyKindOther: '—',
    latencyEmpty: 'Замеров пока нет',
    latencyEmptyHint:
      'За это окно ничего не измерено. Пустая таблица — это не «быстро», а «данных нет».',
    latencyBlind:
      'Не смог прочитать счётчик времени ответа — числа ниже сейчас ничего не значат (это «не проверял», а не «всё хорошо»).',
    latencyQuantileHint:
      'Квантили — верхние границы вёдер: «p95 = 400 мс» значит «95% запросов уложились в 400 мс», а не «ровно 400».',
    latencyThresholdHint: (ms: number, minutes: number) =>
      `Тревога поднимается, когда p95 держится выше ${ms} мс ${minutes} минут подряд.`,
    // ── «Деньги» ─────────────────────────────────────────────────────────
    moneyLoadFailed: 'Не удалось загрузить деньги',
    moneyReviewsLoadFailed: 'Не удалось загрузить перечень разборов',
    moneyReviewAlreadyResolved: 'Этот случай уже разобрали раньше',
    moneyReviewMarked: 'Отмечено разобранным',
    moneyReviewFailed: 'Не удалось отметить разбор',
    moneyReconcileOk: 'Все организации сходятся (обновлено)',
    // Русская фраза оставлена ДОСЛОВНО прежней (WEB-1900 — перенос текста, а
    // не переписывание): менять её склонение значило бы править формулировку
    // заодно с переездом и ломать закрепивший её тест.
    moneyReconcileMismatch: (count: number) =>
      `Расхождения в ${count} организациях`,
    moneyReconcileFailed: 'Не удалось пересверить баланс-инвариант',
    moneyColClient: 'Клиент',
    moneyColAmount: 'Сумма',
    moneyColTier: 'Тариф',
    moneyColStatus: 'Статус',
    moneyStatusSuccess: 'успех',
    moneyTierCutTag: 'tier-срез',
    moneyColTime: 'Время',
    moneyColOrg: 'Организация',
    moneyColDiff: 'Расхождение',
    moneyColProject: 'Проект',
    moneyColModel: 'Модель',
    moneyColWhen: 'Когда',
    moneyColProviderCharged: 'Заплатили провайдеру',
    moneyColWhatBurns: 'Что горит',
    moneyColReference: 'Ключ операции',
    moneyColReason: 'Причина',
    moneyColSince: 'С какого момента',
    moneyResolveButton: 'Разобрано',
    moneyReviewsCardTitle: 'Разборы, ждущие закрытия',
    moneyReviewsLoading: 'Загружаем перечень…',
    moneyReviewsEmpty: 'Неразобранных денежных случаев нет',
    moneyReviewsNote:
      '«Разобрано» ничего не чинит и денег не двигает — это отметка ' +
      '«человек посмотрел и принял решение вне системы». Кто и когда закрыл, сохраняется в самой строке.',
    moneyReviewKindPartialRefund: 'Возврат: решение по кредитам',
    moneyReviewKindTopupUnfulfilled: 'Оплачено, пакет не выдан',
    moneyReviewKindCreditDebt: 'Долг по кредитам',
    // WEB-2347 — второй перечень: что автоматика разобрала сама.
    moneyAutoResolvedCardTitle: 'Разобрано автоматически',
    moneyAutoResolvedWindow: (days: number) =>
      `за последние ${days} ${plural('ru', days, { one: 'день', few: 'дня', many: 'дней' })}`,
    moneyAutoResolvedLoading: 'Загружаем перечень…',
    moneyAutoResolvedEmpty: 'За это окно автоматика ничего не закрывала',
    moneyAutoResolvedLoadFailed:
      'Не удалось загрузить перечень разобранного автоматически',
    moneyAutoResolvedNote:
      'Эти строки действий не требуют: деньги уже вернулись либо кредиты ' +
      'выданы. Перечень нужен, чтобы работа автоматики была видна — закрытая ' +
      'строка пропадает из «Разборов, ждущих закрытия».',
    moneyColOutcome: 'Что сделала автоматика',
    moneyColRefundId: 'Возврат',
    moneyColOriginalRow: 'Исходная строка',
    moneyColClosedAt: 'Когда закрыто',
    moneyOutcomeAutoCredited: 'Кредиты выданы',
    moneyOutcomeAutoRefunded: 'Деньги возвращены',
    moneyOutcomeDuplicate: 'Повтор компенсированного платежа',
    moneyPaymentsCardTitle: 'Платежи платформы',
    moneySuccessful: 'Успешных',
    moneyFailures: 'Провалов',
    moneyTierCuts: 'Tier-срезов',
    moneyPaymentsTotal: 'Всего платежей',
    moneyTierCutNote:
      'Строки с tier-срезом: оплата прошла, но план не активировался. В таблице — последние 100.',
    moneyInvariantTitle: 'Баланс-инвариант',
    moneyReconcileButton: 'Пересверить',
    moneyInvariantOk: 'Все организации сходятся',
    moneyAiCardTitle: 'AI-траты (Anthropic)',
    moneyToday: 'Сегодня',
    moneyWeek: 'Неделя',
    moneyMonth: 'Месяц',
    moneySpendBreakdown: 'Разбивка спенда',
    moneyImages: 'картинки',
    moneyFailedGenNote:
      'FAILED-генерации — заплатили провайдеру, списали с клиента 0',
    moneyNoFailures: 'Провалов не было',
    // ── «Пульс бизнеса» ──────────────────────────────────────────────────
    statusOk: 'ок',
    pulseSectionUnavailable: 'Раздел недоступен',
    pulseBurningNow: 'Горит сейчас',
    pulseUpdatedAt: (time: string) => `Обновлено в ${time}`,
    pulseUpdatedUnknown: 'Ещё не обновлялось',
    pulseRefresh: 'Обновить',
    pulseAllCalm: 'Всё спокойно — критичных сигналов нет',
    pulseMore: 'Подробнее →',
    pulseBusinessSignals: 'Бизнес-сигналы',
    pulseEarningTitle: 'Мы зарабатываем?',
    pulseRevenue24: 'Выручка за 24ч',
    pulseSubsActive: 'Активных подписок',
    pulseGrantSpend: 'Потрачено подарков',
    pulsePayments24: 'Платежи 24ч',
    pulsePayOk: (count: number) => `${count} ок`,
    pulsePayFail: (count: string) => `${count} провал`,
    pulseTierCutsLabel: 'Tier-срезы',
    // Русская фраза дословно прежняя (перенос текста, не переписывание).
    pulseTierCutsCount: (count: number) => `${count} случая`,
    pulseTierCutsNone: 'нет',
    // WEB-2503: второй рубеж цены тарифа — цена подписки не из ценового плана.
    pulseOffPlanPriceLabel: 'Цена не из плана',
    pulseOffPlanPriceCount: (count: number) =>
      `${count} ${plural('ru', count, { one: 'событие', few: 'события', many: 'событий' })}`,
    pulseOffPlanPriceNone: 'нет',
    pulseBalanceInvariant: 'Баланс-инвариант',
    pulseClientsWorkingTitle: 'Клиенты работают?',
    pulseNotFound24: '404-rate воронок 24ч',
    pulseDomains: 'Домены',
    pulseDomainsActive: (count: number) => `${count} активны`,
    pulseDomainsStuck: (count: number) => `${count} завис`,
    pulseDomainsExpiring: (count: number) => `${count} истекают ≤7д`,
    pulseBudgetTitle: 'Не жжём бюджет?',
    pulseAiSpendLink: 'AI-траты',
    pulseSpendToday: 'Спенд сегодня',
    pulseSpendMonth: 'Спенд за месяц',
    pulseBudgetNotSet: 'Бюджет месяца не настроен',
    pulseMarginLeak: 'Margin-leak',
    pulseBurnedGen: 'Сгорело генераций',
    pulseBurnedGenTag: (count: number) => `${count} сгорело`,
    pulseTechTitle: 'Технический пульс',
    pulseTechDb: 'База данных',
    pulseTechQueues: 'Очереди',
    pulseInWork: 'в работе',
    pulseTechFeCrashes: 'Крэши FE 24ч',
    pulseTechBeCrashes: 'Крэши BE 24ч',
    pulseTechLastDeploy: 'Последний деплой',
    pulseTechDbPool: 'Пул БД',
    pulseBusy: 'занято',
    pulseFree: 'свободно',
    pulseWaiting: (count: number) => `${count} в очереди`,
    pulseLegacyGuid: 'Легаси-guid 24ч',
    pulseLegacyBreakdown: (read: number, mutate: number, paywall: number) =>
      `чтение ${read}, деньги ${mutate}, оплата ${paywall}`,
    pulseLegacyUnverified: 'по данным клиента, не проверены:',
    pulseLegacyByProject: 'оплата по проектам:',
    pulseAnalyticsSpill: 'Аналитика вне разделов',
    pulseRotationStalled: 'ротация могла встать',
    // WEB-2670: проверка значения не уложилась в предел ожидания — вместо
    // числа-заглушки, которое сервер присылает на его месте.
    pulseProbeTimedOut: (seconds: number) => `не ответила за ${seconds} с`,
    pulseSpillNotCounted: (seconds: number) => `не досчитан за ${seconds} с`,
    // ── «Аудит» (журнал действий администраторов) ────────────────────────
    auditActionCreateUser: 'Создание клиента',
    auditActionUpdateUser: 'Изменение клиента',
    auditActionDeleteUser: 'Деактивация клиента',
    auditActionRestoreUser: 'Восстановление клиента',
    auditActionChangeUserPassword: 'Смена пароля клиента',
    auditActionViewUser: 'Просмотр данных клиента',
    auditActionCreateProject: 'Создание проекта',
    auditActionUpdateProject: 'Изменение проекта',
    auditActionDeleteProject: 'Удаление проекта',
    auditActionAddProjectMember: 'Добавление участника',
    auditActionUpdateProjectMember: 'Изменение роли участника',
    auditActionRemoveProjectMember: 'Удаление участника',
    auditActionAdjustBalance: 'Корректировка баланса',
    auditActionChargebackUnfreeze: 'Разморозка после чарджбэка',
    auditActionAdminCredit: 'Начисление кредитов',
    auditActionGrantTier: 'Выдача тарифа',
    auditActionTierChange: 'Смена тарифа (автоматическая)',
    auditActionQueueRetry: 'Повтор задачи очереди',
    auditActionRevokePreviewLinks: 'Отзыв ссылок предпросмотра',
    auditActionPricingPlanVersionCreated: 'Сохранена версия ценового плана',
    auditActionEnterpriseAddonInvoiceCreated: 'Выставлен счёт допродажи Enterprise',
    auditEntityUser: 'Клиент',
    auditEntityProject: 'Проект',
    auditEntityProjectMember: 'Участник проекта',
    auditEntityBalance: 'Баланс клиента',
    auditEntitySubscription: 'Организация',
    auditEntityQueue: 'Очередь',
    auditEntityPricingPlan: 'Ценовой план (организация)',
    auditDetailSelfService:
      'Самообслуживание администратора (состоит в организации)',
    auditDetailTargetIsAdmin: 'Цель — администратор системы',
    auditDetailSelf: 'Действие над собственной учётной записью',
    auditDetailAmountCents: 'Сумма, центы',
    auditDetailDescription: 'Причина',
    auditDetailProjectId: 'Проект',
    auditDetailOrgId: 'Организация',
    auditDetailLotId: 'Лот',
    auditDetailFromTier: 'Тариф до',
    auditDetailToTier: 'Тариф после',
    auditDetailTargetUserId: 'Клиент (id)',
    auditDetailTargetUserEmail: 'Почта клиента',
    auditDetailEmail: 'Почта',
    auditDetailUsername: 'Имя',
    auditDetailSystemRole: 'Системная роль',
    auditDetailQueueName: 'Очередь',
    auditDetailRole: 'Роль',
    auditDeletedBadge: 'удалён',
    auditLoadFailed: 'Не удалось загрузить журнал аудита',
    auditColTime: 'Время',
    auditColAdmin: 'Админ',
    auditColAction: 'Действие',
    auditColEntity: 'Объект',
    auditColTarget: 'Над кем',
    auditDetailsButton: 'Детали',
    auditFilterAllAdmins: 'Все админы',
    auditFilterAllActions: 'Все действия',
    auditEmpty: 'Нет записей',
    auditDrawerTitle: 'Детали события',
    auditEntityTypeLabel: 'Тип объекта',
    auditTargetClient: 'Клиент',
    auditEventContent: 'Содержание события',
    auditNoDetails: 'Пишущий не оставил подробностей',
    // ── «Биллинг» (поиск клиента, баланс, снятие заморозки) ──────────────
    billingSearchFailed: 'Не удалось найти клиента',
    billingSearchCardTitle: 'Поиск клиента и баланс',
    billingEmailPlaceholder: 'Email клиента',
    billingSearchButton: 'Найти',
    billingSearchEmpty: 'Введите email и нажмите «Найти»',
    billingColRole: 'Роль',
    billingSelectButton: 'Выбрать',
    billingBalanceOf: (email: string) => `Баланс: ${email}`,
    billingAdjustBalance: 'Скорректировать баланс',
    billingChargebackCardTitle: 'Chargeback — разморозка',
    billingProjectIdPlaceholder: 'ID проекта',
    billingUnfreezeConfirmTitle: 'Разморозить проект?',
    billingUnfreezeConfirmOk: 'Подтвердить',
    billingUnfreezeButton: 'Разморозить',
    billingUnfrozen: 'Проект разморожен',
    billingUnfreezeFailed: 'Не удалось разморозить проект',
    billingUnfreezeReasonRequired:
      'Спор у платёжной системы не закрыт — снятие заморозки требует причины.',
    billingReasonModalTitle: 'Причина снятия заморозки',
    billingReasonSubmit: 'Снять заморозку',
    billingReasonPlaceholder:
      'Например: спор улажен с банком напрямую, клиент прислал письмо банка',
    billingReasonHint: (min: number, max: number) =>
      `От ${min} до ${max} символов. Причина уйдёт в журнал организации и владельцу письмом.`,
    // WEB-1346 (заход 2): кредиты за оспоренные платежи и отметка о списании.
    billingCreditsLoadFailed:
      'Не удалось узнать, сколько кредитов выдано за оспоренные платежи',
    billingWriteOffModalTitle: 'Кредиты за оспоренные платежи',
    billingWriteOffIntro:
      'За платежи по этим спорам выдавались кредиты. Сами они не списываются: проверьте баланс и отметьте, сделано ли списание.',
    billingWriteOffUnknownNote:
      'По части споров сумму выяснить не удалось (платёжная система не ответила). Отметка всё равно обязательна.',
    billingWriteOffColDispute: 'Спор',
    billingWriteOffColStatus: 'Статус',
    billingWriteOffColGranted: 'Выдано',
    billingWriteOffColRemaining: 'Не потрачено',
    billingWriteOffStatusOpen: 'открыт',
    billingWriteOffStatusLost: 'проигран',
    billingWriteOffUnknown: 'сумма неизвестна',
    billingWriteOffTotal: (sum: string) => `Всего известно: ${sum}`,
    billingWriteOffDone: 'Списание сделано',
    billingWriteOffNotRequired: 'Списание не требуется',
    billingWriteOffSubmit: 'Снять заморозку',
    // ── Организации клиента (выбор в окнах денег и тарифа) ───────────────
    orgsLoadFailed: 'Не удалось найти организации клиента',
    orgRoleOwner: 'владелец',
    orgRoleAdmin: 'админ',
    orgRoleMember: 'участник',
    orgProjectCount: (count: number) =>
      `${count} ${plural('ru', count, { one: 'проект', few: 'проекта', many: 'проектов' })}`,

    // ── Окно «История входов» ────────────────────────────────────────────
    loginMethodPassword: 'пароль',
    loginMethodTwoFactor: 'двухфакторный код',
    loginColTime: 'Время',
    loginColDevice: 'Устройство',
    loginColMethod: 'Способ входа',
    loginHistoryTitle: (userLabel: string) => `История входов — ${userLabel}`,
    loginHistoryNotice:
      'Журнал ведётся с момента его появления в системе. Входы, случившиеся раньше, в него не попали.',
    loginHistoryLoadFailed: 'Не удалось загрузить историю входов',
    loginHistoryEmptyError: 'Историю входов загрузить не удалось',
    loginHistoryEmpty: 'Ни одного входа не зафиксировано',
    // ── Окна денег и тарифа (общие поля выбора организации) ──────────────
    modalClient: 'Клиент',
    modalOrganization: 'Организация',
    modalOrgRequired: 'Выберите организацию',
    modalOrgSearching: 'Ищем организации…',
    modalOrgPlaceholder: 'Выберите организацию',
    modalOrgNotFound: 'У клиента нет организаций',
    modalReason: 'Причина',
    modalReasonRequired: 'Укажите причину',

    // ── Окно «Корректировка баланса» ─────────────────────────────────────
    adjustAmountRequired: 'Укажите сумму',
    adjustAmountWholeCredits: 'Укажите целое число кредитов (1 Cr = 1 цент)',
    adjustAmountCentPrecision: 'Укажите сумму с точностью до цента',
    adjustAmountNonZero: 'Сумма корректировки не может быть нулевой',
    adjustAmountMaxDollars: (max: string) =>
      `Сумма не может превышать $${max} за одну операцию`,
    adjustAmountMaxCredits: (max: string) =>
      `Сумма не может превышать ${max} Cr за одну операцию`,
    adjustDone: (amount: string) => `Баланс скорректирован: ${amount}`,
    adjustFailed: 'Не удалось скорректировать баланс',
    adjustModalTitle: 'Корректировка баланса',
    adjustApply: 'Применить',
    adjustOrgHint:
      'Кошелёк общий на организацию — корректировка применяется к её балансу.',
    adjustUnit: 'Единица',
    adjustAmountLabel: (unit: string) => `Сумма (± в ${unit})`,
    adjustUnitDollars: 'долларах',
    adjustUnitCredits: 'кредитах',
    adjustPreviewCredit: 'Будет зачислено',
    adjustPreviewDebit: 'Будет списано',
    adjustPreviewRate: (credits: number) =>
      `${credits} Cr (курс 1 Cr = 1 цент)`,

    // ── Окно «Выдать/сменить тариф» ──────────────────────────────────────
    grantDone: (tier: string) => `Тариф ${tier} применён`,
    grantStripeConflict:
      'У клиента активная Stripe-подписка, сначала отмените её.',
    grantFailed: 'Не удалось выдать тариф',
    grantModalTitle: 'Выдать/сменить тариф',
    grantSubmit: 'Выдать',
    grantOrgHint:
      'Организация, чью подписку меняем (вручную, без Stripe). Владельческие — первыми.',
    grantTierLabel: 'Тариф',
    grantTierRequired: 'Выберите тариф',
    grantReasonHint: 'Попадёт в журнал «Аудит» вместе с вашим именем.',
    modalLoading: 'Загрузка…',
    // ── Панель баланса клиента ───────────────────────────────────────────
    balanceLoadFailed: 'Не удалось загрузить баланс',
    balanceTotalDeposited: (amount: string) => `Всего пополнений: ${amount}`,
    balanceRecentTransactions: 'Последние транзакции',
    balanceEmpty: 'Нет транзакций',
    balanceColDate: 'Дата',
    balanceColAmount: 'Сумма',
    balanceColStatus: 'Статус',
    balanceColType: 'Тип',
    balanceColNote: 'Примечание',
    transactionStatusPending: 'в процессе',
    transactionStatusCompleted: 'завершена',
    transactionStatusFailed: 'провалена',
    // ── Окно «Регистрация клиента» ───────────────────────────────────────
    registerRoleUser: 'Пользователь',
    registerRoleAdmin: 'Администратор',
    registerBalanceNotNumber: 'Стартовый баланс должен быть числом',
    registerBalanceNegative:
      'Стартовый баланс не может быть отрицательным. 0 или пусто — регистрация без начисления',
    registerBalanceTooBig:
      'Стартовый баланс за одну регистрацию не может превышать $100 000',
    registerBalanceTooPrecise:
      'Не более двух знаков после запятой: суммы меньше цента не бывает',
    registerPasswordTooShort: 'Минимум 8 символов',
    registerAdminConfirmTitle: 'Регистрация администратора',
    registerAdminConfirmText:
      'Вы создаёте пользователя с правами администратора (полный доступ к панели). Подтвердить?',
    registerDone: 'Клиент зарегистрирован',
    registerFailed: 'Не удалось зарегистрировать клиента',
    registerModalTitle: 'Регистрация клиента',
    registerSubmit: 'Зарегистрировать',
    registerEmailRequired: 'Укажите email',
    registerEmailInvalid: 'Введите корректный email',
    registerPasswordLabel: 'Пароль',
    registerPasswordHint: 'можно задать позже',
    registerPasswordPlaceholder: 'можно оставить пустым',
    registerUsernameLabel: 'Имя пользователя',
    registerEmailVerifiedLabel: 'Email подтверждён',
    registerRoleLabel: 'Роль',
    registerOrgNameLabel: 'Название организации',
    registerFromEmailHint: 'по умолчанию — из email',
    registerProjectNameLabel: 'Название проекта',
    registerInitialBalanceLabel: 'Стартовый баланс, $',
    registerInitialBalanceHint:
      '0 или пусто — без начисления, отрицательное не принимается. 1 $ = 100 кредитов: начисление придёт кредитами. Не больше $100 000 за одну регистрацию. Не более двух знаков после запятой.',
    // ── «Проекты» ────────────────────────────────────────────────────────
    projectsLoadFailed: 'Не удалось загрузить проекты',
    projectsMembersLoadFailed: 'Не удалось загрузить участников',
    projectsUpdated: 'Проект обновлён',
    projectsUpdateFailed: 'Не удалось обновить проект',
    projectsDeleted: 'Проект удалён',
    projectsDeleteFailed: 'Не удалось удалить проект',
    projectsRoleUpdated: 'Роль обновлена',
    projectsRoleUpdateFailed: 'Не удалось обновить роль',
    projectsMemberRemoved: 'Участник удалён',
    projectsMemberRemoveFailed: 'Не удалось удалить участника',
    projectsColProject: 'Проект',
    projectsColMembers: 'Участники',
    projectsColCreated: 'Создан',
    projectsSearchPlaceholder: 'Поиск по названию проекта',
    projectsEmpty: 'Нет проектов',
    projectsNameLabel: 'Название',
    projectsNameRequired: 'Укажите название проекта',
    projectsDescriptionLabel: 'Описание',
    projectsMembersTitle: 'Участники',
    projectsMembersLoadError: (reason: string) =>
      `Не удалось загрузить участников: ${reason}`,
    projectsMembersEmpty: 'Нет участников',
    projectsColRole: 'Роль',
    projectsRemoveMemberConfirm: 'Удалить участника?',
    projectsDeleteConfirm: 'Удалить проект?',
    // WEB-2114 (требование 24): массовый отзыв ссылок предпросмотра.
    previewLinksTitle: 'Ссылки предпросмотра',
    previewLinksRevokeProjectButton: 'Отозвать все ссылки предпросмотра',
    previewLinksRevokeProjectConfirm:
      'Отозвать все ссылки предпросмотра этого проекта? Они сразу перестанут открываться у всех.',
    previewLinksTargetHint:
      'Либо только ссылки одного квиза, пейволла или воронки. У квиза и пейволла вместе с ними отзываются ссылки воронок этого проекта, в которых он стоит сейчас:',
    previewLinksTargetQuiz: 'Квиз',
    previewLinksTargetPaywall: 'Пейволл',
    previewLinksTargetFunnel: 'Воронка',
    previewLinksTargetIdPlaceholder: 'Идентификатор объекта (uuid)',
    previewLinksRevokeTargetButton: 'Отозвать ссылки объекта',
    previewLinksRevokeTargetConfirm:
      'Отозвать все ссылки предпросмотра этого объекта? У квиза и пейволла — ещё и ссылки воронок этого проекта, в которых он стоит сейчас. Они сразу перестанут открываться у всех.',
    previewLinksRevoked: (count: number) =>
      `Отозвано ссылок предпросмотра: ${count}`,
    previewLinksRevokeFailed: 'Не удалось отозвать ссылки предпросмотра',
    projectsDeleteButton: 'Удалить проект',
    // ── «Клиенты» ────────────────────────────────────────────────────────
    clientsDeactivateWarningMaterials:
      'Материалы клиента будут заперты в режим «только чтение», а публичная выдача погаснет: воронки перестанут принимать трафик и собирать лиды. Данные при этом не удаляются, домены остаются за клиентом, действие обратимо кнопкой «Восстановить».',
    clientsDeactivateWarningSubscriptionBold:
      'Клиента с живой подпиской Stripe деактивировать нельзя.',
    clientsDeactivateWarningSubscriptionRest:
      ' Деактивация подписку не отменяет, поэтому пока по клиенту может уйти списание, она отклоняется: вход ему закрылся бы, а отменить подписку он уже не смог бы. Сначала сотрудник поддержки отменяет подписку вручную в панели Stripe платформы: Customers → карточка клиента → Subscriptions → Cancel subscription — после этого деактивация проходит. Подписку, которую клиент сам отменил «в конце оплаченного периода», она не держит.',
    clientsReportLocked: (projects: number) =>
      `Материалы заперты в режим «только чтение»: проектов — ${projects}. Публичная выдача погашена.`,
    clientsReportNotLocked:
      'Вход закрыт, но материалы НЕ заперты — воронки клиента продолжают отдавать трафик. Повторите деактивацию; если не помогает, это сбой на сервере (в журнале строка MANUAL-RECONCILE).',
    clientsReportSkippedOrgs: (count: number) =>
      `Не тронуто организаций: ${count} — в них есть другие участники, и их материалы остались в работе.`,
    clientsReportKeptDomains: (count: number) =>
      `Купленных доменов остаётся за клиентом: ${count} — мы их не освобождаем.`,
    clientsReportLiveSubscriptions: (count: number) =>
      `Подписок, по которым Stripe ещё может списать: ${count}. `,
    clientsReactivationUnlocked: (projects: number) =>
      `Материалы возвращены в работу: проектов — ${projects}.`,
    clientsReactivationNotUnlocked:
      'Вход возвращён, но материалы НЕ отперты — воронки клиента остаются погашенными. Повторите восстановление; если не помогает, это сбой на сервере (в журнале строка MANUAL-RECONCILE).',
    clientsTiersLoading: 'загрузка…',
    clientsTierUnknown: 'неизвестно',
    clientsLoadFailed: 'Не удалось загрузить клиентов',
    clientsDetailRefreshFailed: 'Не удалось обновить данные клиента',
    clientsDeactivated: 'Клиент деактивирован',
    clientsDeactivatedTitle: 'Клиент деактивирован',
    clientsUnderstood: 'Понятно',
    clientsDeactivateFailed: 'Не удалось деактивировать клиента',
    clientsRestored: 'Клиент восстановлен',
    clientsRestoredTitle: 'Клиент восстановлен',
    clientsRestoreFailed: 'Не удалось восстановить клиента',
    clientsPasswordUpdated: 'Пароль обновлён',
    clientsPasswordChangeFailed: 'Не удалось сменить пароль',
    clientsColRole: 'Роль',
    clientsColEmailVerified: 'Email подтверждён',
    clientsColStatus: 'Статус',
    clientsStatusDeactivated: 'деактивирован',
    clientsStatusActive: 'активен',
    clientsColRegistered: 'Регистрация',
    clientsColLastLogin: 'Последний вход',
    clientsNeverSignedIn: 'ни разу не заходил',
    clientsLoginHistoryTooltip: 'История входов',
    clientsSearchPlaceholder: 'Поиск по email / имени',
    clientsFilterActive: 'Активные',
    clientsFilterDeactivated: 'Деактивированные',
    clientsFilterAll: 'Все статусы',
    clientsRegisterButton: 'Зарегистрировать клиента',
    clientsEmpty: 'Нет клиентов',
    clientsDetailUsername: 'Имя пользователя',
    clientsDetailOrgTiers: 'Тариф организаций',
    clientsBalanceDivider: 'Баланс',
    clientsAdminTargetWarning:
      'Это учётная запись администратора системы — действия над ней записываются в журнал. Если это ваша собственная запись, смена пароля сразу завершит текущий сеанс: потребуется войти заново с новым паролем.',
    clientsChangePassword: 'Сменить пароль',
    clientsAdjustBalance: 'Скорректировать баланс',
    clientsGrantTier: 'Выдать/сменить тариф',
    clientsDeactivateConfirm: 'Деактивировать клиента?',
    clientsDeactivateButton: 'Деактивировать',
    clientsRestoreButton: 'Восстановить',
    clientsPasswordModalTitle: 'Сменить пароль',
    clientsNewPasswordLabel: 'Новый пароль',
    clientsNewPasswordRequired: 'Укажите новый пароль',
    // ── Отказы служебных ручек, показываемые человеком-читаемой фразой ───
    admin2faRequired:
      'Панель управления теперь требует двухфакторную аутентификацию. Включите её в настройках аккаунта (Настройки → Безопасность), затем обновите страницу.',
    navMigration: 'Переезд на subster.ai',
    migrationLoadFailed: 'Не удалось загрузить сводку переезда',
    migrationDomainsTitle: 'Домены клиентов (свой CNAME)',
    migrationDomainsLegacyLabel: 'На старой цели CNAME',
    migrationProjectsTitle: 'Обращения к старому адресу API за 7 дней',
    migrationProjectsLabel: 'Проектов на старом API',
    navNotifications: 'Уведомления',
    notificationsHint:
      'Администраторы платформы и какие письма каждый получает. У кого настройка не сохранена — получает (как раньше).',
    notificationsAdminColumn: 'Администратор',
    notificationsConveyorMail: 'Письма конвейера',
    notificationsLoadFailed: 'Не удалось загрузить настройки уведомлений',
    notificationsSaveFailed: 'Не удалось сохранить настройку',
    migrationOldAddresses: (targets: string, hosts: string): string =>
      `Старые цели CNAME: ${targets}. Старые хосты API: ${hosts}.`,
    migrationNotSet: 'не заданы',
    migrationCnameOff:
      'LEGACY_CNAME_TARGETS пуста — домены не проверяются, пустой список ничего не доказывает.',
    migrationApiOff:
      'LEGACY_API_HOSTS пуста — обращения к старому API не отмечаются, пустой список ничего не доказывает.',
    migrationColDomain: 'Домен',
    migrationColOrg: 'Организация',
    migrationColProject: 'Проект',
    migrationColVerdict: 'Куда смотрит',
    migrationColResolvedTo: 'Ответ DNS',
    migrationColCheckedAt: 'Проверяли',
    migrationColLastSeen: 'Последнее обращение',
    migrationColHits24h: 'Обращений за сутки',
    migrationColHits7d: 'Обращений за 7 дней',
    migrationColHost: 'Хост',
    migrationVerdictLegacy: 'На старой цели',
    migrationVerdictCurrent: 'Переехал',
    migrationVerdictOther: 'Смотрит в другое место',
    migrationVerdictMissing: 'Записи нет',
    migrationVerdictUnknown: 'Ещё не проверен',
    migrationLastAttemptFailed: 'последняя попытка: DNS не ответил',
    migrationCheckNow: 'Проверить сейчас',
    migrationCheckDone: 'Проверено',
    migrationCheckFailed: 'Не удалось проверить домен',
    migrationNoDomains: 'Своих доменов клиентов через Cloudflare for SaaS нет',
    migrationNoProjects: 'За 7 дней на старый адрес API никто не обращался',
    // WEB-2329: база возврата в приложение у проекта (см. AdminMigrationPage).
    migrationBaseTitle: 'База возврата в приложение у проекта',
    migrationBaseHint:
      'От этой базы строится ссылка «вернуться в приложение» после оплаты. Старую базу ставим только проекту, у которого в уже выпущенном приложении прописана прежняя зона (Associated Domains / intent-filter) — иначе ссылка приложение не откроет.',
    migrationBaseSearchPlaceholder: 'Название проекта',
    migrationBaseSearchButton: 'Найти',
    migrationBaseColCurrent: 'База возврата',
    migrationBaseColProjectId: 'Идентификатор проекта',
    migrationBaseDefaultOption: (main: string): string =>
      `Основная база (${main})`,
    migrationBaseNoProjects: 'По этому запросу проектов не нашлось',
    migrationBaseSaved: 'База возврата сохранена',
    migrationBaseSaveFailed: 'Не удалось сохранить базу возврата',
    migrationBaseLoadFailed: 'Не удалось загрузить список проектов',
  },
);
